'use client';

import { CatSkeleton } from '@blocks-react/components/Skeleton';
import {
  Conditional,
  Map,
  MapControl,
  getBounds,
  getLatLng
} from '../../common';
import { DealerStore } from '../dealerStoreType';
import { CatButton } from '@blocks-react/components/Button';
import { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import styles from '../DealerLocator.module.scss';
import { ACTIVE_MARKER_ICON, INACTIVE_MARKER_ICON } from './constants';
import {
  DEFAULT_MAP_BOUNDS,
  DEFAULT_MAP_ZOOM,
  MAP_MARKER_MAX_WIDTH,
  MAP_MARKER_MIN_WIDTH,
  MOBILE_MAP_HEIGHT_OFFSET,
  SELECTED_STORE_ID,
  DEFAULT_MAP_HEIGHT
} from '../constants';
import { roundCompare } from '../utils';
import { CatHeading } from '@blocks-react/components/Heading';
import { useFormContext } from 'react-hook-form';
import './dealer-locator-map-container.scss';
import { RenderMapMarker } from './render-map-marker';

export const getMarkerContentElement = (
  isActiveStore: boolean,
  dealerName: string,
  storeName: string
): HTMLDivElement => {
  const container = document.createElement('div');
  container.classList.add('map-marker-container');
  container.setAttribute('data-testid', 'custom-marker-content');

  if (isActiveStore) {
    container.classList.add('active');
  }

  const titleEl = document.createElement('div');
  titleEl.className = 'tooltip-title';
  titleEl.textContent = dealerName;

  const subtitleEl = document.createElement('div');
  subtitleEl.className = 'tooltip-subtitle';
  subtitleEl.textContent = storeName;
  if (dealerName?.length > 10) {
    titleEl.textContent = dealerName.substring(0, 14) + '...';
  } else {
    titleEl.textContent = dealerName;
  }

  container.appendChild(titleEl);
  container.appendChild(subtitleEl);

  return container;
};

interface DealerLocatorMapContainerProps {
  userCountryCode: string;
  dealerCode: string;
  dealerStores: DealerStore[];
  selectedStore: DealerStore;
  onSearchThisAreaClick: (arg1: { lat: number; lng: number }) => void;
  searchThisAreaBtnText: string;
  loadingResults: boolean;
  isStoreLocationPinEnhancementFlag?: boolean;
  isMobile?: boolean;
  isMobileFullMapViewEnabled?: boolean;
}

export const DealerLocatorMapContainer = ({
  userCountryCode,
  dealerCode,
  dealerStores,
  selectedStore,
  onSearchThisAreaClick,
  searchThisAreaBtnText,
  loadingResults,
  isStoreLocationPinEnhancementFlag = false,
  isMobile = false,
  isMobileFullMapViewEnabled = false
}: DealerLocatorMapContainerProps) => {
  const [center, setCenter] = useState({ lat: 0, lng: 0 });
  const [zoom, setZoom] = useState(DEFAULT_MAP_ZOOM);
  const [bounds, setBounds] = useState(DEFAULT_MAP_BOUNDS);
  const [showSearchThisAreaBtn, setShowSearchThisAreaBtn] = useState(false);
  const [showMarkerDetails, setShowMarkerDetails] = useState(false);
  const initialCenter = useRef({ lat: 0, lng: 0 });
  const activeMarker = useRef('');
  const { setValue } = useFormContext();
  const dealerInfoStyles = cx(styles['dealer-locator-text'], 'mb-0');
  const markerInfoOptions = {
    minWidth: MAP_MARKER_MIN_WIDTH,
    maxWidth: MAP_MARKER_MAX_WIDTH
  };

  const isSelectedStoreInDealerStores =
    (dealerStores.includes(selectedStore) && dealerStores[0]) || !selectedStore;
  const visibleStores = isSelectedStoreInDealerStores
    ? dealerStores
    : [...dealerStores.slice(0, dealerStores?.length - 1), selectedStore];

  // Set new bounds, active marker, and center for map when a new location is selected
  useEffect(() => {
    if (selectedStore === undefined) {
      activeMarker.current = '';
    }
    const isNewLocation =
      dealerStores?.length &&
      !!selectedStore &&
      selectedStore.id.toString() !== activeMarker.current;
    if (isNewLocation) {
      setShowMarkerDetails(false);
      setShowSearchThisAreaBtn(false);
      const newBounds = getBounds(
        dealerStores.map((store) => {
          return getLatLng(store.geo.latitude, store.geo.longitude);
        })
      );
      setBounds(newBounds);
      activeMarker.current = selectedStore.id.toString();
      const closestStore = getLatLng(
        selectedStore.geo.latitude,
        selectedStore.geo.longitude
      );
      setCenter(closestStore);
      initialCenter.current = { lat: closestStore.lat, lng: closestStore.lng };
    }
  }, [dealerStores, selectedStore]);

  // show search this area button if the center is has been moved from the initial map center
  useEffect(() => {
    if (
      initialCenter.current.lat &&
      initialCenter.current.lng &&
      !showSearchThisAreaBtn
    ) {
      const isInitialCenter =
        roundCompare(initialCenter?.current.lat, center.lat, 7) &&
        roundCompare(initialCenter?.current.lng, center.lng, 7);
      if (!isInitialCenter && center.lat && center.lng) {
        setShowSearchThisAreaBtn(true);
      }
    }
  }, [center, showSearchThisAreaBtn]);

  const handleActiveMarker = (store?: DealerStore) => {
    if (store) {
      activeMarker.current = store.id.toString();
      setValue(SELECTED_STORE_ID, store.id.toString());
      setShowMarkerDetails(true);
      setCenter({ lat: store.geo.latitude + 0.8, lng: store.geo.longitude });
    } else {
      setShowMarkerDetails(false);
    }
  };

  const getContentElement = (isActiveStore: boolean) => {
    const storeIcon = isActiveStore ? ACTIVE_MARKER_ICON : INACTIVE_MARKER_ICON;
    const storeIconElement = document.createElement('img');
    storeIconElement.src = storeIcon;
    return storeIconElement;
  };

  const isUpdatedMapPin = isStoreLocationPinEnhancementFlag && isMobile;
  return (
    <div
      className={cx({
        'my-3': !isMobileFullMapViewEnabled && !isMobile,
        [styles['map-container-mobile']]: isMobileFullMapViewEnabled && isMobile
      })}
      style={{
        height:
          isMobileFullMapViewEnabled && isMobile
            ? `calc(100vh - ${MOBILE_MAP_HEIGHT_OFFSET})`
            : DEFAULT_MAP_HEIGHT
      }}
      data-testid="map-container"
    >
      <Conditional
        test={!loadingResults && !!selectedStore}
        fallback={
          <div className={styles['map-container']}>
            <CatSkeleton size="xxl" />
          </div>
        }
      >
        <Map
          userCountryCode={userCountryCode}
          dealerCode={dealerCode}
          zoom={zoom}
          onZoomChange={setZoom}
          center={center}
          onCenterChange={() => setCenter}
          bounds={bounds}
          className={styles['map-container']}
          onClick={() => handleActiveMarker()}
          mapOptions={{
            disableDefaultUI: true,
            clickableIcons: false,
            fullscreenControl: false,
            zoomControl: true
          }}
        >
          <MapControl
            isMobileFullMapViewEnabled={isMobileFullMapViewEnabled}
            isMobile={isMobile}
          >
            <CatButton
              {...(!(isMobileFullMapViewEnabled && isMobile) && { variant: 'contrast' })}
              className={cx('m-2', {
                'd-none': !showSearchThisAreaBtn,
                [styles['search-this-area-btn']]:
                  isMobileFullMapViewEnabled && isMobile
              })}
              onClick={() => onSearchThisAreaClick(center)}
            >
              {searchThisAreaBtnText}
            </CatButton>
          </MapControl>
          {visibleStores?.map((store) => {
            const {
              id,
              geo: { latitude, longitude },
              dealerName,
              name,
              address: { city, state, phone, line1 }
            } = store;

            const isActiveStore = id.toString() === activeMarker.current;

            const storeZIndex = isActiveStore ? 1 : 0;
            const storeIconElement = isUpdatedMapPin
              ? getMarkerContentElement(isActiveStore, dealerName, name)
              : getContentElement(isActiveStore);

            const commonMarkerProps = {
              id,
              latitude,
              longitude,
              isActiveStore,
              showMarkerDetails,
              handleActiveMarker,
              store,
              storeIconElement,
              storeZIndex,
              markerInfoOptions,
              isUpdatedMapPin
            };
            return (
              <div key={id}>
                <Conditional
                  test={isUpdatedMapPin}
                  fallback={
                    <RenderMapMarker {...commonMarkerProps}>
                      {
                        <div
                          className={`cat-u-theme-typography-body-sm ${styles['map-details-container']}`}
                        >
                          <CatHeading
                            variant="label-lg"
                            className="cat-u-theme-typography-label-lg"
                          >
                            {dealerName}
                          </CatHeading>
                          {city && <p className={dealerInfoStyles}>{city}</p>}{' '}
                          {line1 && <p className={dealerInfoStyles}>{line1}</p>}{' '}
                          {city && state && (
                            <p
                              className={dealerInfoStyles}
                            >{`${city}, ${state}`}</p>
                          )}
                          {phone && <p className={dealerInfoStyles}>{phone}</p>}{' '}
                        </div>
                      }
                    </RenderMapMarker>
                  }
                >
                  <RenderMapMarker {...commonMarkerProps} />
                </Conditional>
              </div>
            );
          })}
        </Map>
      </Conditional>
    </div>
  );
};
